import { useState, forwardRef, useImperativeHandle, useMemo, useCallback } from 'react';
import { DialogModal, Modal } from '@/components';
import { useApi } from '@/api/useApi';
import { useMutationProvider } from '@/hooks';
import { toast } from 'react-toastify';
import { Phone } from 'lucide-react';
import { AppointmentData } from '../appointment-list/appointment-list.types';
import { ManualCalendarSlotUpdateRequestLiveServiceTypeEnum } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import '@/assets/styles/components/call-modal.styles.scss';
import { checkPermissionStatus, requestCameraAndMicPermission, permissionStates } from '@/utility/permission';
import { parseJwt } from '@/utility';
import { useAuthStore } from '@/zustand/auth/store.ts';

export interface CallModalRef {
	handleCallStudent: (appointment: AppointmentData) => void;
	handleDirectCall: (phoneNumber: string) => void;
}

interface CallModalProps {
	liveServiceType?: ManualCalendarSlotUpdateRequestLiveServiceTypeEnum;
}

export const CallModal = forwardRef<CallModalRef, CallModalProps>((props, ref) => {
	const [isCallConfirmationOpen, setIsCallConfirmationOpen] = useState(false);
	const [isCallModalOpen, setIsCallModalOpen] = useState(false);
	const [selectedStudentForCall, setSelectedStudentForCall] = useState<AppointmentData | null>(null);
	const [studentPhoneNumber, setStudentPhoneNumber] = useState<string>('');
	const [iframeKey, setIframeKey] = useState<string>('');

	const { StudentControllerApi } = useApi();

	const { data: authData } = useAuthStore();

	const expertId = useMemo(() => (authData?.access_token ? parseJwt(authData?.access_token)?.additional_info?.expert_id : ''), [authData?.access_token]);

	const base64Encode = (str: string): string => {
		return btoa(unescape(encodeURIComponent(str)));
	};

	const iframeUrl = useMemo(() => {
		if (!studentPhoneNumber || !selectedStudentForCall || !iframeKey) return '';

		return `https://sip2.ecalltr.com/dpsip/?cache=${iframeKey}&d=${base64Encode(studentPhoneNumber)}&ad=${base64Encode(selectedStudentForCall?.studentName || '')}&uzmanID=${base64Encode(expertId)}&ogrenciId=${base64Encode(selectedStudentForCall?.studentId?.toString() || '')}`;
	}, [studentPhoneNumber, selectedStudentForCall, expertId, iframeKey]);

	const findStudentPhonesMutation = useMutationProvider(
		(studentId: number) => {
			return StudentControllerApi.findStudentPhones(studentId, props.liveServiceType || ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.OneOnOneCoaching);
		},
		{
			onSuccess: (response: any) => {
				const phoneNumber = response?.data?.data?.phoneNumber?.length < 11 ? `0${response?.data?.data?.phoneNumber}` : response?.data?.data?.phoneNumber;
				if (phoneNumber) {
					setStudentPhoneNumber(phoneNumber);
					setIframeKey(Date.now().toString());
					setIsCallModalOpen(true);
				} else {
					toast.error('Öğrencinin telefon numarası bulunamadı');
				}
			},
			onError: () => {
				toast.error('Telefon numarası alınamadı');
			}
		}
	);

	const handleCallConfirmation = useCallback(
		(confirmed: boolean) => {
			if (confirmed && selectedStudentForCall) {
				findStudentPhonesMutation.mutate(selectedStudentForCall.studentId);
			}
			setIsCallConfirmationOpen(false);
		},
		[selectedStudentForCall, findStudentPhonesMutation]
	);

	const handleCloseCallModal = useCallback(() => {
		setIsCallModalOpen(false);
		setSelectedStudentForCall(null);
		setStudentPhoneNumber('');
		setIframeKey('');
	}, []);

	const handleCallStudentFromParent = useCallback((appointment: AppointmentData) => {
		setSelectedStudentForCall(appointment);
		setIsCallConfirmationOpen(true);
	}, []);

	const handleDirectCallFromParent = useCallback(async (phoneNumber: string) => {
		const check = await checkPermissionStatus();
		if (check?.camera !== permissionStates.granted || check?.microphone !== permissionStates.granted) {
			const result = await requestCameraAndMicPermission();
			if (!result) {
				toast.error('Arama yapabilmek için kamera ve mikrofon izni gereklidir.');
				return;
			}
		}

		const formattedPhone = phoneNumber.length < 11 ? `0${phoneNumber}` : phoneNumber;
		setSelectedStudentForCall({
			appointmentId: 0,
			appointmentTime: '',
			appointmentStartDate: '',
			appointmentEndDate: '',
			appointmentStatus: 'CREATED',
			studentName: 'Bilinmeyen',
			studentSurname: '',
			studentId: 0,
			topicId: 0,
			completedAppointmentCount: 0,
			studentGradeId: 0,
			studentGradeName: '',
			isTopRanked: undefined
		});
		setStudentPhoneNumber(formattedPhone);
		setIframeKey(Date.now().toString());
		setIsCallModalOpen(true);
	}, []);

	useImperativeHandle(
		ref,
		() => ({
			handleCallStudent: handleCallStudentFromParent,
			handleDirectCall: handleDirectCallFromParent
		}),
		[handleCallStudentFromParent, handleDirectCallFromParent]
	);

	const modalContent = useMemo(
		() => (
			<div
				className="call-modal-content"
				onMouseDown={e => e.stopPropagation()}
				onClick={e => e.stopPropagation()}>
				<div className="call-modal-custom-header">
					<div className="call-icon">
						<Phone size={20} />
					</div>
					<h3>Öğrenci Araması</h3>
					<div className="header-buttons">
						<button
							className="btn-close-modal"
							onClick={handleCloseCallModal}
							title="Kapat">
							<i className="icons-close" />
						</button>
					</div>
				</div>
				{iframeUrl && (
					<div
						className="call-iframe-container"
						onMouseDown={e => e.stopPropagation()}>
						<iframe
							key={iframeKey}
							src={iframeUrl}
							title="Telefon Araması"
							className="call-iframe"
							allow="microphone"
							frameBorder="0"
							scrolling="no"
						/>
					</div>
				)}
			</div>
		),
		[iframeUrl, iframeKey, handleCloseCallModal]
	);

	return (
		<>
			<DialogModal
				isOpen={isCallConfirmationOpen}
				onClose={() => handleCallConfirmation(false)}
				variant="danger"
				title="Öğrenciyi Aramak İstiyor Musunuz?"
				primaryButtonText="Evet"
				secondaryButtonText="Hayır"
				shouldCloseOnOverlayClick={false}
				onPrimaryClick={() => handleCallConfirmation(true)}
				onSecondaryClick={() => handleCallConfirmation(false)}
				className="call-confirmation-modal">
				<p className="modal-text">
					{selectedStudentForCall && (
						<>
							<strong>
								{selectedStudentForCall.studentName} {selectedStudentForCall.studentSurname}
							</strong>{' '}
							öğrencisini aramak istediğinizden emin misiniz?
						</>
					)}
				</p>
			</DialogModal>
			<Modal
				isOpen={isCallModalOpen}
				onClose={handleCloseCallModal}
				size={'lg'}
				variant="bottom"
				className="call-modal"
				hasHeader={false}
				showCloseButton={false}
				closeOnClickOutside={false}>
				{modalContent}
			</Modal>
		</>
	);
});
